import React, { useEffect, useRef } from "react";
import { pickColor, rgba, hexToRgb } from "./internal/colorUtils";

export const key = "phyllotaxis";

const DEFAULT_BG = "#0a0e1f";
const DEFAULT_INNER = "#f59e0b";
const DEFAULT_OUTER = "#ef4444";
const DEFAULT_HIGHLIGHT = "#fde68a";

// Golden-angle phyllotaxis. Place the n-th seed at
//   r(n) = c · √n,  θ(n) = n · φ   where  φ = π·(3 − √5) ≈ 137.5°.
// The √n radius keeps the seed density uniform and the irrational
// rotation φ guarantees no two consecutive seeds line up — the result is
// the spiral arrangement seen in sunflower heads, pinecones, pineapples,
// and just about every plant that needs to pack growth efficiently.
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
const SPACING_FRAC = 0.020;     // c, expressed as a fraction of min(W, H)
const MAX_DOTS = 750;
const GROWTH_RATE = 60;         // dots added per second during the bloom
const ANGLE_DRIFT = 0.000018;   // rad/ms — slow perturbation once full;
                                // even a tiny offset visibly re-organizes
                                // the spiral arms because of φ's
                                // irrationality

function blendRgba(hexA, hexB, t, alpha) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return rgba(hexA, alpha);
  const clamp = Math.max(0, Math.min(1, t));
  const r = Math.round(a.r + (b.r - a.r) * clamp);
  const g = Math.round(a.g + (b.g - a.g) * clamp);
  const bl = Math.round(a.b + (b.b - a.b) * clamp);
  return `rgba(${r}, ${g}, ${bl}, ${alpha})`;
}

export default function Phyllotaxis({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([
    DEFAULT_BG,
    DEFAULT_INNER,
    DEFAULT_OUTER,
    DEFAULT_HIGHLIGHT,
  ]);
  const stateRef = useRef({
    count: 0,
    lastGrowAt: 0,
    angleOffset: 0,
  });

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_INNER),
      pickColor(colors, 2, DEFAULT_OUTER),
      pickColor(colors, 3, DEFAULT_HIGHLIGHT),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const tick = (ts) => {
      const [bg, innerCol, outerCol, highlightCol] = colorsRef.current;
      const state = stateRef.current;
      if (!state.lastGrowAt) state.lastGrowAt = ts;

      // Grow the bloom one seed at a time, paced by real time so it
      // looks the same on 30 Hz and 144 Hz displays.
      if (state.count < MAX_DOTS) {
        const interval = 1000 / GROWTH_RATE;
        while (state.count < MAX_DOTS && ts - state.lastGrowAt >= interval) {
          state.count += 1;
          state.lastGrowAt += interval;
        }
      } else {
        // Bloom is complete — perturb the angle so the spiral arms
        // shimmer slowly. φ is irrational; even a sub-microradian
        // offset visibly re-routes the arms over a few seconds.
        state.angleOffset += ANGLE_DRIFT * 16; // approx per-frame at 60Hz
      }

      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      const cx = width / 2;
      const cy = height / 2;
      const minDim = Math.min(width, height);
      const c = minDim * SPACING_FRAC;
      const baseTheta = state.angleOffset;
      const maxR = c * Math.sqrt(MAX_DOTS);

      // Single pass: place each dot, color/blend by radius, give the
      // freshest few a brief halo so the growth is visible.
      for (let n = 0; n < state.count; n += 1) {
        const r = c * Math.sqrt(n);
        const theta = n * GOLDEN_ANGLE + baseTheta;
        const x = cx + Math.cos(theta) * r;
        const y = cy + Math.sin(theta) * r;
        const radialT = Math.min(1, r / maxR);
        // Inner seeds are tighter and slightly larger; outer ones taper
        // so the bloom fades into the canvas edges naturally.
        const dotR = 1.6 + (1 - radialT) * 1.6;
        const alpha = 0.55 + (1 - radialT) * 0.35;
        ctx.fillStyle = blendRgba(innerCol, outerCol, radialT, alpha);
        ctx.beginPath();
        ctx.arc(x, y, dotR, 0, Math.PI * 2);
        ctx.fill();

        // The most recently added 8 seeds get a brief halo so the
        // bloom's growth front is clearly visible. After the bloom is
        // full this branch never fires.
        if (n >= state.count - 8 && state.count < MAX_DOTS) {
          const haloR = dotR * 4;
          const halo = ctx.createRadialGradient(x, y, 0, x, y, haloR);
          halo.addColorStop(0, rgba(highlightCol, 0.55));
          halo.addColorStop(1, rgba(highlightCol, 0));
          ctx.fillStyle = halo;
          ctx.beginPath();
          ctx.arc(x, y, haloR, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
